const NO_POSITIONS = [];

/**
 * @typedef {{
 *   id: string|number,
 *   isLong: boolean,
 *   entry: { price: number, time: number|null, label: string },
 *   card: { leverageText: string, pnl: number|null, pnlText: string },
 *   levels: Array<{ tone: string, price: number, text: string }>,
 *   zones: Array<{ tone: string, price: number, direction: string }>,
 * }} ChartPosition - tone: POSITION_TONE, direction: POSITION_DIRECTION
 */

// What the WebGL layer draws: lines (they start after their pills, so pill texts count) and
// zones. The card texts and PnL are the text layer only
const geometryKeyOf = (positions, symbol, isVisible) =>
  JSON.stringify([
    symbol,
    isVisible,
    positions.map(({ id, isLong, entry, levels, zones }) => [
      id,
      isLong,
      entry.price,
      entry.label,
      levels.map(({ tone, price, text }) => [tone, price, text]),
      zones.map(({ tone, price, direction }) => [tone, price, direction]),
    ]),
  ]);

const isSameHighlight = (first, second) =>
  first === second ||
  Boolean(first && second && first.id === second.id && first.isPressed === second.isPressed);

/**
 * Open positions on the chart (engine.positions). The engine knows nothing
 * about Redux: positions, their texts and the close action come from outside.
 * A change of prices redraws the frame; a change of PnL or texts only the
 * text layer, so the live PnL does not make anything flicker.
 *
 * @param {object} params
 * @param {() => void} params.invalidate - schedules a frame
 * @param {() => void} params.redrawText - redraws the text layer alone
 */
export const createPositionsState = ({ invalidate, redrawText }) => {
  let positions = NO_POSITIONS;
  let symbol = null;
  let isVisible = true;
  let geometryKey = "";
  let closeButton = { label: "", onClose: null };
  let closeButtons = [];
  let highlight = null;

  const update = () => {
    const nextKey = geometryKeyOf(positions, symbol, isVisible);

    if (nextKey === geometryKey) {
      redrawText();
      return;
    }

    geometryKey = nextKey;
    invalidate();
  };

  return {
    /**
     * @param {ChartPosition[]} nextPositions - open positions of `nextSymbol`
     * @param {string} nextSymbol - pair slug; shown only while the chart is on it
     */
    setPositions: (nextPositions, nextSymbol) => {
      positions = nextPositions;
      symbol = nextSymbol;
      update();
    },

    /** @param {boolean} nextIsVisible - the "P&L lines" toggle */
    setVisible: (nextIsVisible) => {
      isVisible = nextIsVisible;
      update();
    },

    /** @param {{ label: string, onClose: ((id: string|number) => void) | null }} button - tooltip, action */
    setCloseButton: (button) => {
      closeButton = button;
    },

    /**
     * @param {{ symbol: string|null, store: { length: number } }} data - DataController
     * @returns {ChartPosition[]} positions to draw: none while hidden, without bars or on another pair
     */
    shownFor: (data) =>
      isVisible && data.store.length > 0 && data.symbol === symbol ? positions : NO_POSITIONS,

    get closeLabel() {
      return closeButton.label;
    },

    /** Close buttons of the last text draw: [{ positionId, rect }] in CSS px. */
    get closeButtons() {
      return closeButtons;
    },

    setCloseButtons: (buttons) => {
      closeButtons = buttons;
    },

    /** Close button under the mouse or pressed: { id, isPressed }, null for none. */
    get highlight() {
      return highlight;
    },

    setHighlight: (nextHighlight) => {
      if (isSameHighlight(highlight, nextHighlight)) return;

      highlight = nextHighlight;
      redrawText();
    },

    /** @param {string|number} id */
    close: (id) => closeButton.onClose?.(id),
  };
};
